import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Dropdown from '@/Components/Dropdown/Dropdown';
import { usePremiumModal } from '@/Hooks/usePremiumModal';
import HorizontalSeparator from '@/Components/Shared/HorizontalSeparator';
import Switch from '@/Components/Switch/Switch';
import { FeatureStatus, naturalSort, LocalPrefKey } from '@standardnotes/snjs';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Subtitle, Title, Text } from '@/Components/Preferences/PreferencesComponents/Content';
import PreferencesPane from '../PreferencesComponents/PreferencesPane';
import PreferencesGroup from '../PreferencesComponents/PreferencesGroup';
import PreferencesSegment from '../PreferencesComponents/PreferencesSegment';
import { PremiumFeatureIconName } from '@/Components/Icon/PremiumFeatureIcon';
import EditorAppearance from './Appearance/EditorAppearance';
import { GetAllThemesUseCase } from '@standardnotes/ui-services';
import { useLocalPreference } from '@/Hooks/usePreference';
import { c } from 'ttag';
const Appearance = ({ application }) => {
    const premiumModal = usePremiumModal();
    const [themeItems, setThemeItems] = useState([]);
    const [autoLightTheme, setAutoLightTheme] = useLocalPreference(LocalPrefKey.AutoLightThemeIdentifier);
    const [autoDarkTheme, setAutoDarkTheme] = useLocalPreference(LocalPrefKey.AutoDarkThemeIdentifier);
    const [useDeviceSettings, setUseDeviceSettings] = useLocalPreference(LocalPrefKey.UseSystemColorScheme);
    const [useTranslucentUI, setUseTranslucentUI] = useLocalPreference(LocalPrefKey.UseTranslucentUI);
    const toggleTranslucentUI = () => {
        setUseTranslucentUI(!useTranslucentUI);
    };
    useEffect(() => {
        const usecase = new GetAllThemesUseCase(application.items);
        const { thirdParty, native } = usecase.execute({ excludeLayerable: true });
        const dropdownItems = [];
        dropdownItems.push({
            label: c('B6.Preferences.Appearance.Label').t `Default`,
            value: 'Default',
        });
        dropdownItems.push(...native.map((theme) => {
            return {
                label: theme.displayName,
                value: theme.featureIdentifier,
                icon: application.features.getFeatureStatus(theme.uniqueIdentifier) !== FeatureStatus.Entitled
                    ? PremiumFeatureIconName
                    : undefined,
            };
        }));
        dropdownItems.push(...thirdParty.map((theme) => {
            return {
                label: theme.displayName,
                value: theme.featureIdentifier,
            };
        }));
        setThemeItems(naturalSort(dropdownItems, 'label'));
    }, [application]);
    const toggleUseDeviceSettings = () => {
        setUseDeviceSettings(!useDeviceSettings);
        if (!application.preferences.getLocalValue(LocalPrefKey.AutoLightThemeIdentifier)) {
            setAutoLightTheme(autoLightTheme);
        }
        if (!application.preferences.getLocalValue(LocalPrefKey.AutoDarkThemeIdentifier)) {
            setAutoDarkTheme(autoDarkTheme);
        }
        setUseDeviceSettings(!useDeviceSettings);
    };
    const changeAutoLightTheme = (value) => {
        const item = themeItems.find((item) => item.value === value);
        if (item && item.icon === PremiumFeatureIconName) {
            premiumModal.activate(`${item.label} theme`);
            return;
        }
        setAutoLightTheme(value);
    };
    const changeAutoDarkTheme = (value) => {
        const item = themeItems.find((item) => item.value === value);
        if (item && item.icon === PremiumFeatureIconName) {
            premiumModal.activate(`${item.label} theme`);
            return;
        }
        setAutoDarkTheme(value);
    };
    return (_jsxs(PreferencesPane, { children: [_jsx(PreferencesGroup, { children: _jsxs(PreferencesSegment, { children: [_jsx(Title, { children: c('B6.Preferences.Appearance.Title').t `Themes` }), _jsxs("div", { className: "mt-2", children: [_jsxs("div", { className: "flex justify-between gap-2 md:items-center", children: [_jsxs("div", { className: "flex flex-col", children: [_jsx(Subtitle, { children: c('B6.Preferences.Appearance.Subtitle').t `Disable translucent UI` }), _jsx(Text, { children: c('B6.Preferences.Appearance.Info')
                                                        .t `Use opaque style for UI elements instead of translucency` })] }), _jsx(Switch, { onChange: toggleTranslucentUI, checked: !useTranslucentUI })] }), _jsx(HorizontalSeparator, { classes: "my-4" }), _jsxs("div", { className: "flex justify-between gap-2 md:items-center", children: [_jsxs("div", { className: "flex flex-col", children: [_jsx(Subtitle, { children: c('B6.Preferences.Appearance.Subtitle').t `Use system color scheme` }), _jsx(Text, { children: c('B6.Preferences.Appearance.Info')
                                                        .t `Automatically change active theme based on your system settings.` })] }), _jsx(Switch, { onChange: toggleUseDeviceSettings, checked: useDeviceSettings })] }), _jsx(HorizontalSeparator, { classes: "my-4" }), _jsxs("div", { children: [_jsx(Subtitle, { children: c('B6.Preferences.Appearance.Subtitle').t `Automatic Light Theme` }), _jsx(Text, { children: c('B6.Preferences.Appearance.Info').t `Theme to be used for system light mode:` }), _jsx("div", { className: "mt-2", children: _jsx(Dropdown, { label: c('B6.Preferences.Appearance.Label').t `Select the automatic light theme`, items: themeItems, value: autoLightTheme, onChange: changeAutoLightTheme, disabled: !useDeviceSettings }) })] }), _jsx(HorizontalSeparator, { classes: "my-4" }), _jsxs("div", { children: [_jsx(Subtitle, { children: c('B6.Preferences.Appearance.Subtitle').t `Automatic Dark Theme` }), _jsx(Text, { children: c('B6.Preferences.Appearance.Info').t `Theme to be used for system dark mode:` }), _jsx("div", { className: "mt-2", children: _jsx(Dropdown, { label: c('B6.Preferences.Appearance.Label').t `Select the automatic dark theme`, items: themeItems, value: autoDarkTheme, onChange: changeAutoDarkTheme, disabled: !useDeviceSettings }) })] })] })] }) }), _jsx(EditorAppearance, { application: application })] }));
};
export default observer(Appearance);
